import { queryOptions, QueryClient, useQuery } from "@tanstack/react-query";

import { api, Location, Page, PublishedNavigationElement } from "@/lib/api";
import { unwrapAxiosData } from "@/lib/api/query-utils";
import { keys as sharedKeys } from "@/features/shared/hooks/queries";
import { keys as widgetKeys } from "@/features/dashboard/features/widgets/hooks/queries";

const keys = {
  publishedNavigationElements: (dashboardId: string) =>
    ["publishedNavigationElements", dashboardId] as const,
  publishedPage: (dashboardId: string, pageId: string) =>
    ["publishedPage", dashboardId, pageId] as const,
  publishedPagesForDashboard: (dashboardId: string) =>
    ["publishedPage", dashboardId] as const,
  publishedLocations: (dashboardId: string) => ["publishedLocations", dashboardId] as const,
};

/**
 * Drops every `/published/...` cache that a publication replaces.
 *
 * Removal rather than invalidation: a publication mints new ids, so the
 * previous snapshot is not merely stale, it is unusable. An invalidated query
 * still hands its cached data to the next render while it refetches, which
 * paints the dashboard from the previous publication and fires widget data
 * requests for ids the backend no longer knows (404, and the widget throws).
 *
 * This also covers the original complaint: one QueryClient lives per tab, with
 * a 100s staleTime and no refetch on window focus, so walking from the CMS into
 * the dashboard app in the same tab used to need a hard refresh.
 *
 * Removing fires no requests; the dashboard app refetches when it next mounts
 * these queries. Publishing happens from the CMS, so nothing is mounted here.
 */
export function removePublishedDashboardQueries(
  queryClient: QueryClient,
  dashboardId: string,
) {
  queryClient.removeQueries({
    queryKey: keys.publishedNavigationElements(dashboardId),
  });
  queryClient.removeQueries({
    queryKey: keys.publishedPagesForDashboard(dashboardId),
  });
  queryClient.removeQueries({
    queryKey: keys.publishedLocations(dashboardId),
  });
  // Slug, time settings and warning flags reach the dashboard app through the
  // published list, which is one cache entry for all dashboards.
  queryClient.removeQueries({
    queryKey: sharedKeys.publishedDashboards(),
  });
  // Keyed by published widget id, so these entries belong to the publication we
  // just replaced.
  queryClient.removeQueries({
    queryKey: widgetKeys.publishedWidgetDataNamespace(),
  });
}

export const publishedNavigationElementsQuery = (dashboardId?: string) =>
  queryOptions({
    queryKey: keys.publishedNavigationElements(String(dashboardId)),
    queryFn: (): Promise<PublishedNavigationElement[]> =>
      unwrapAxiosData(
        api.published.getNavigationElementsForPublishedDashboard(String(dashboardId)),
      ),
    enabled: Boolean(dashboardId),
  });

export const usePublishedNavigationElementsQuery = (dashboardId?: string) =>
  useQuery(publishedNavigationElementsQuery(dashboardId));

export const usePublishedPageQuery = (dashboardId: string, pageId: string) => {
  return useQuery({
    queryKey: keys.publishedPage(dashboardId, pageId),
    queryFn: (): Promise<Page> =>
      unwrapAxiosData(api.published.getPageForPublishedDashboard(dashboardId, pageId)),
    enabled: !!dashboardId && !!pageId,
  });
};

export const usePublishedLocationsQuery = (dashboardId?: string) => {
  return useQuery({
    queryKey: keys.publishedLocations(String(dashboardId)),
    queryFn: (): Promise<Location[]> =>
      unwrapAxiosData(api.published.getLocationsForPublishedDashboard(String(dashboardId))),
    enabled: Boolean(dashboardId),
  });
};
